import type { FrontModule } from "@nabble/sdk";
import type { IconName } from "@nabble/ui";

export interface AppSection {
  id: string;
  label: string;
  icon: IconName;
  path: string;
  description?: string;
}

/** What users see as a "module": a name, an icon and its sub-sections. */
export interface AppModule {
  id: string;
  label: string;
  icon: IconName;
  description: string;
  sections: AppSection[];
}

/**
 * An organization's own administration: its people and its name, for the administrators of that
 * organization (docs/adr/0017). Part of the core, presented like a module under Administration.
 */
export const ORGANIZATION_APP: AppModule = {
  id: "organization",
  label: "Organization",
  icon: "briefcase",
  description: "The people who work in your organization, their roles, and its name.",
  sections: [
    { id: "organization.people", label: "People", icon: "users", path: "/organization/people", description: "Add people and choose their role" },
    { id: "organization.details", label: "Details", icon: "edit", path: "/organization/details", description: "The organization's name" },
  ],
};

/** Modules shown under "Administration" in the menu (both belong to the core). */
export const ADMINISTRATION_IDS: ReadonlySet<string> = new Set(["organization", "admin"]);

/** The Admin Center belongs to the core, but it is presented like a module. */
export const ADMIN_CENTER: AppModule = {
  id: "admin",
  label: "Admin Center",
  icon: "settings",
  description: "Manage people, their organizations and permissions, the enabled modules and the security rules.",
  sections: [
    { id: "admin.users", label: "Users", icon: "users", path: "/admin/users", description: "Invite people and manage their access" },
    { id: "admin.organizations", label: "Organizations", icon: "briefcase", path: "/admin/organizations", description: "Group people into organizations" },
    { id: "admin.roles", label: "Roles", icon: "key", path: "/admin/roles", description: "Group permissions by module" },
    { id: "admin.modules", label: "Modules", icon: "layers", path: "/admin/modules", description: "Enable or disable modules" },
    { id: "admin.policies", label: "Policies", icon: "sliders", path: "/admin/policies", description: "Passwords, sessions and retention" },
    {
      id: "admin.data-requests",
      label: "Data requests",
      icon: "shield",
      path: "/admin/data-requests",
      description: "Answer personal data requests on time",
    },
    { id: "admin.branding", label: "Branding", icon: "image", path: "/admin/branding", description: "Logos and icons of the suite and the website" },
  ],
};

/**
 * Groups each visible FrontModule's menu entries (filtered by permission)
 * under the module, and appends the Organization section for organization
 * administrators and the Admin Center for suite administrators.
 */
export function buildAppModules(frontModules: FrontModule[], permissions: string[]): AppModule[] {
  const out: AppModule[] = frontModules
    .map((m) => {
      const sections = m.menu
        .filter((entry) => !entry.permission || permissions.includes(entry.permission))
        .map((entry) => ({
          id: entry.id,
          label: entry.label,
          icon: (entry.icon ?? "grid") as IconName,
          path: entry.path,
          description: entry.description,
        }));
      return {
        id: m.id,
        label: m.label ?? m.menu[0]?.label ?? m.id,
        icon: (m.icon ?? m.menu[0]?.icon ?? "grid") as IconName,
        description: m.description ?? "",
        sections,
      };
    })
    .filter((m) => m.sections.length > 0);
  if (permissions.includes("core.organization")) out.push(ORGANIZATION_APP);
  if (permissions.includes("core.admin")) out.push(ADMIN_CENTER);
  return out;
}
